/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getTransformObject } from '~/common/photos/transforms'
import { keysToCamel } from '~/utility/toCamel'
import { ReviewsFetchError, ReviewsValidationError } from './ReviewsError'
import type {
  AjaxResponseReview,
  FilterCounts,
  PhotoResultUrl,
  ReviewFilters,
  ReviewInput,
  ReviewsInput,
  ReviewsResponse,
  Theme,
} from './types'

// Default timeout for reviews (30 seconds - standard endpoint)
const REVIEWS_TIMEOUT = 30000

// Raw AJAX response interface from server
interface AjaxResponse {
  reviews: AjaxResponseReview[]
  page: number
  themes_breakdown: Theme[]
  ratings: any
  available_group_types: any[]
  available_languages: any[]
  available_unit_types: any[]
  total_count: number
}

export class ReviewsRepositoryFetch {
  async readOneById(langCode: string, { campsiteSlug, id }: ReviewInput) {
    const url = this.getUrl(langCode, { campsiteSlug, reviewId: id })
    const reviewParams = { reviewId: id }
    let response: AjaxResponse

    // Step 1: Fetch the data
    try {
      response = await $fetch<AjaxResponse>(url, {
        timeout: REVIEWS_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return null for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return null
      }

      // Throw custom fetch error - don't log here, let the store handle logging to avoid duplicates
      throw new ReviewsFetchError(url, campsiteSlug, reviewParams, error)
    }

    // Step 2: Validate response structure and process
    try {
      if (!response?.reviews || !Array.isArray(response.reviews)) {
        throw new Error(
          'Invalid response structure: missing or invalid reviews array',
        )
      }
      const reviews = this.mapResponseReviews(response.reviews)
      return reviews.length ? reviews[0] : null
    } catch (error: any) {
      const validationError = new ReviewsValidationError(
        url,
        campsiteSlug,
        reviewParams,
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }

  async readAll(
    langCode: string,
    { campsiteSlug, filters, order, page, limit }: ReviewsInput,
  ): Promise<ReviewsResponse> {
    const url = this.getUrl(langCode, {
      filters,
      order,
      limit,
      campsiteSlug,
      page,
    })
    const reviewParams = { filters, order, page, limit }
    let response: AjaxResponse

    // Step 1: Fetch the data
    try {
      response = await $fetch<AjaxResponse>(url, {
        timeout: REVIEWS_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty response for abort errors - they're intentional cancellations
      // This is common when users navigate away from review pages quickly
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return this.getEmptyResponse()
      }

      // Throw custom fetch error - don't log here, let the store handle logging to avoid duplicates
      throw new ReviewsFetchError(url, campsiteSlug, reviewParams, error)
    }

    // Step 2: Validate response structure and process
    try {
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response structure: response is not an object')
      }
      if (!response.reviews || !Array.isArray(response.reviews)) {
        throw new Error(
          'Invalid response structure: missing or invalid reviews array',
        )
      }

      return {
        ...response,
        page: Number(response.page) || 1,
        themeBreakdown: {
          top: getTopTheme(response.themes_breakdown || []),
          themes: response.themes_breakdown || [],
        },
        filterCounts: getFilterCounts(response),
        ratings: keysToCamel(response.ratings || {}),
        reviews: this.mapResponseReviews(response.reviews),
      }
    } catch (error: any) {
      const validationError = new ReviewsValidationError(
        url,
        campsiteSlug,
        reviewParams,
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }

  private getEmptyResponse(): ReviewsResponse {
    return {
      total_count: 0,
      page: 1,
      themes_breakdown: [],
      ratings: {},
      reviews: [],
      available_group_types: [],
      available_languages: [],
      available_unit_types: [],
      themeBreakdown: {
        top: null,
        themes: [],
      },
      filterCounts: {
        groupType: [],
        language: [],
        unitType: [],
      },
      summary: {
        rate_recommend: 0,
        rate_revisit: 0,
      },
      summary_text: null,
    }
  }

  private mapResponseReviews(reviews: AjaxResponseReview[]) {
    return reviews.map((r) => ({
      id: r.id,
      addedAt: new Date(r.timestamp).toISOString(),
      booked: r.is_booked,
      comment: r.comment,
      country: r.country,
      liked: r.liked,
      disliked: r.disliked,
      groupType: r.group_type,
      isAnonymous: r.is_anonymous,
      isMobile: r.is_mobile,
      language: r.language
        ? {
            id: r.language.id.toString(),
            code: r.language.code,
          }
        : null,
      photos: r.photos.map((photo) => ({
        caption: photo.caption,
        url: getTransformObject<PhotoResultUrl>(
          'review',
          photo.url.masterImage,
        ),
        titleAttribution: photo.title_attribution || '',
      })),
      pitchtype: r.pitchtype,
      rateOverall: r.rate_overall,
      rateLocation: r.rate_location,
      rateValue: r.rate_value,
      rateQuality: r.rate_quality,
      rateFacilities: r.rate_facilities,
      rateService: r.rate_service,
      rateChildFriendly: r.rate_child_friendly,
      rateClean: r.rate_clean,
      rateWifi: r.rate_wifi,
      recommend: r.recommend,
      revisit: r.revisit,
      timestamp: r.timestamp,
      title: r.title,
      unitType: r.unit_type,
      user: r.user,
      site: r.site,
      userRating: r.user_rating,
      visitDate: r.visit_date,
    }))
  }

  private getUrl(
    langCode: string,
    {
      filters,
      order,
      limit,
      campsiteSlug,
      page,
      reviewId,
    }: {
      filters?: ReviewFilters
      order?: string
      limit?: number
      campsiteSlug: string
      page?: number
      reviewId?: string
    },
  ) {
    const params = new URLSearchParams()
    if (order) {
      params.append('o', order)
    }
    if (limit) {
      params.append('limit', limit.toString())
    }
    if (filters?.rating) {
      params.append('overall', filters.rating)
    }
    if (filters?.groupType) {
      params.append('group_type', filters.groupType)
    }
    if (filters?.unitType) {
      params.append('unit_type', filters.unitType)
    }
    if (filters?.language) {
      params.append('language', filters.language)
    }
    if (reviewId) {
      params.append('review', reviewId)
    }
    if (page && page > 1) {
      params.append('page', page.toString())
    }
    if (filters?.has_photos) {
      params.append('has_photos', filters.has_photos)
    }
    const url = `/_/reviews/campsite/${campsiteSlug}/?${params.toString()}`
    return getLangFallbackUrl(langCode, url)
  }
}

function getTopTheme(themesBreakdown: Theme[]) {
  return themesBreakdown.length
    ? themesBreakdown.reduce((max, theme) =>
        max.mean > theme.mean ? max : theme,
      )
    : null
}

function getFilterCounts(response: AjaxResponse): FilterCounts {
  return {
    groupType: response.available_group_types,
    language: response.available_languages,
    unitType: response.available_unit_types,
  }
}
